import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { CalendlyInline } from '../components/Calendly';
import ConsultationWizard from '../components/ConsultationWizard';
import { Reveal } from '../components/Reveal';
import { Star, WhatsAppIcon } from '../components/icons/WhatsAppIcon';
import { WHATSAPP_URL } from '../components/ContactForm';
import { Calendar, Phone, Clock, CheckCircle2, Lightbulb, Coffee, Sparkles } from 'lucide-react';
import { useSeo } from '../hooks/useSeo';
import { PAGE_SEO } from '../seo/site';

const BENEFITS = [
  { icon: Clock, title: '30 Minuten', description: 'Kompaktes Kennenlernen deiner Situation' },
  { icon: Lightbulb, title: 'Individuelle Analyse', description: 'Wir schauen uns deine aktuellen Systeme an' },
  { icon: CheckCircle2, title: 'Konkrete Empfehlungen', description: 'Du erhältst erste Handlungsempfehlungen' },
  { icon: Coffee, title: 'Unverbindlich', description: 'Keine Verpflichtungen, kein Verkaufsdruck' },
];

const TOPICS = [
  'Kassensystem & Kartenzahlung',
  'Reservierungssystem',
  'Personal- und Schichtplanung',
  'Online-Bestellungen & Lieferung',
  'Social Media & Marketing',
  'Website & Online-Bewertungen',
];

const ErstberatungPage = () => {
  useSeo(PAGE_SEO.erstberatung);
  const [searchParams] = useSearchParams();
  const [showCalendly, setShowCalendly] = useState(false);
  const [showWizard, setShowWizard] = useState(false);

  useEffect(() => {
    if (searchParams.get('verify_token')) setShowWizard(true);
  }, [searchParams]);

  useEffect(() => {
    if (!showCalendly) return;
    setTimeout(() => {
      document.getElementById('calendly')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  }, [showCalendly]);

  return (
    <>
      <Header />
      <main data-testid="erstberatung-page">

        {/* Hero */}
        <section className="sec sec--purple relative overflow-hidden">
          <Reveal className="wrap grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <span className="badge-ink badge-ink--lilac mb-6 md:mb-8"><Star />Kostenlose Erstberatung</span>
              <h1 className="display-1 max-w-[18ch] mb-6">
                Lass uns über deine <span className="serif-it text-lilac">digitale Zukunft</span> sprechen
              </h1>
              <p className="text-lg opacity-90 max-w-[56ch] mb-10">
                In einem 30-minütigen Gespräch analysieren wir gemeinsam deine aktuelle Situation
                und zeigen dir, welche digitalen Tools deinen Betrieb voranbringen können.
              </p>

              <div className="cta-row mb-6">
                <button type="button" onClick={() => setShowCalendly(true)} className="btn btn--white" data-testid="hero-calendly-btn">
                  <Calendar className="w-5 h-5" />
                  Termin buchen
                </button>
                <a href="tel:+491787261209" className="btn btn--ghost-off" data-testid="hero-call-btn">
                  <Phone className="w-5 h-5" />
                  Direkt anrufen
                </a>
                <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="btn btn--wa" data-testid="hero-whatsapp-btn">
                  <WhatsAppIcon />
                  WhatsApp
                </a>
              </div>

              <div className="flex items-center gap-4 my-6 max-w-xl">
                <div className="flex-1 h-px bg-off/40" />
                <span className="label-caps text-lilac">oder</span>
                <div className="flex-1 h-px bg-off/40" />
              </div>

              <button
                type="button"
                onClick={() => setShowWizard(true)}
                className="btn btn--lilac"
                data-testid="digital-consultation-btn"
              >
                <Sparkles className="w-5 h-5" />
                Digitale Erstberatung
              </button>
            </div>

            <div className="hidden lg:block lg:col-span-5">
              <figure className="polaroid polaroid--r2 max-w-[440px] ml-auto">
                <div className="polaroid__media aspect-[3/2]">
                  <img src="/hero-2.jpg" alt="Persönliche Beratung vor Ort" width="1080" height="720" className="absolute inset-0 w-full h-full object-cover" />
                </div>
                <figcaption>
                  <span className="polaroid__name block">Persönliche Beratung vor Ort</span>
                  <div className="polaroid__cat">30 Minuten · kostenfrei · unverbindlich</div>
                </figcaption>
              </figure>
            </div>
          </Reveal>
        </section>

        {/* Benefits */}
        <section className="sec bg-off">
          <Reveal className="wrap">
            <h2 className="display-2 mb-12 md:mb-16">Was dich <span className="serif-it text-purple">erwartet</span></h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {BENEFITS.map((benefit, index) => {
                const Icon = benefit.icon;
                return (
                  <div key={benefit.title} className="card-ink card-ink--hover p-6 md:p-7" data-testid={`benefit-${index}`}>
                    <span className="inline-flex w-12 h-12 items-center justify-center border-2 border-ink bg-purple text-off mb-5">
                      <Icon className="w-6 h-6" />
                    </span>
                    <h3 className="font-display font-bold text-2xl mb-2">{benefit.title}</h3>
                    <p className="text-[0.98rem]">{benefit.description}</p>
                  </div>
                );
              })}
            </div>
          </Reveal>
        </section>

        {/* Topics */}
        <section className="sec sec--lilac">
          <Reveal className="wrap grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-7">
              <h2 className="display-2 max-w-[18ch] mb-5">Worüber wir sprechen können</h2>
              <p className="text-lg mb-8 max-w-[56ch]">
                Egal ob du gerade erst anfängst oder deine bestehenden Systeme optimieren möchtest –
                wir beraten dich zu allen digitalen Themen in der Gastronomie.
              </p>
              <ul className="rule-list">
                {TOPICS.map((topic) => (
                  <li key={topic} className="flex items-center gap-3 font-display font-semibold text-lg md:text-xl">
                    <Star />{topic}
                  </li>
                ))}
              </ul>
            </div>
            <div className="lg:col-span-5 card-ink card-ink--shadow p-7 md:p-9">
              <div className="flex items-start gap-4 mb-6">
                <span className="inline-flex w-12 h-12 items-center justify-center border-2 border-ink bg-purple text-off flex-shrink-0">
                  <span className="font-display font-extrabold text-lg">M</span>
                </span>
                <div>
                  <p className="font-display font-bold leading-tight">Maximilian von gastro-beratung.de</p>
                  <p className="text-ink/70 text-[0.95rem]">Dein persönlicher Ansprechpartner</p>
                </div>
              </div>
              <blockquote className="serif-it text-xl md:text-2xl leading-snug mb-8">
                „Jeder Gastrobetrieb ist einzigartig. In unserem Gespräch nehme ich mir die Zeit,
                deine spezifische Situation zu verstehen und dir passende Lösungen vorzuschlagen –
                ohne Fachchinesisch, ohne Druck."
              </blockquote>
              <div className="flex flex-col gap-4">
                <button type="button" onClick={() => setShowWizard(true)} className="btn btn--purple btn--block" data-testid="topics-digital-btn">
                  <Sparkles className="w-5 h-5" />
                  Digitale Erstberatung
                </button>
                <button type="button" onClick={() => setShowCalendly(true)} className="btn btn--block" data-testid="topics-calendly-btn">
                  <Calendar className="w-5 h-5" />
                  Jetzt Termin vereinbaren
                </button>
              </div>
            </div>
          </Reveal>
        </section>

        {/* Calendly */}
        {showCalendly && (
          <section id="calendly" className="sec bg-off" data-testid="calendly-section">
            <div className="wrap max-w-4xl">
              <h2 className="display-2 mb-3">Such dir einen passenden Zeitpunkt aus – wir freuen uns auf das Gespräch</h2>
              <p className="text-lg mb-10">Such dir einen passenden Zeitpunkt aus</p>
              <div className="card-ink overflow-hidden">
                <CalendlyInline height={700} />
              </div>
            </div>
          </section>
        )}

        {/* Alternative contact */}
        <section className="sec sec--deep">
          <Reveal className="wrap">
            <h2 className="display-2 mb-4">Lieber direkt <span className="serif-it text-lilac">sprechen?</span></h2>
            <p className="text-lg opacity-90 max-w-[48ch] mb-10">Kein Problem! Ruf uns an oder schreib uns eine WhatsApp-Nachricht.</p>
            <div className="cta-row">
              <a href="tel:+491787261209" className="btn btn--white" data-testid="alt-call-btn">
                <Phone className="w-5 h-5" />
                +49 178 726 1209
              </a>
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="btn btn--wa" data-testid="alt-whatsapp-btn">
                <WhatsAppIcon />
                WhatsApp
              </a>
            </div>
          </Reveal>
        </section>

      </main>
      <Footer />

      <ConsultationWizard isOpen={showWizard} onClose={() => setShowWizard(false)} />
    </>
  );
};

export default ErstberatungPage;
